import { Dictionary } from 'vuex';

import { Storage } from '@/enums/storage.enum';
import { Login } from '@/interfaces/login';
import { authResource, AuthResource } from '@/resources/auth.resource';
import { profileResource } from '@/resources/profile.resource';
import { tryParseInt } from '@/utils/number';
import { AbstractModule } from './abstract.module';

/**
 * Moduł autentykacji
 *
 * @class AuthModule
 * @extends {AbstractModule<AuthResource>}
 */
class AuthModule extends AbstractModule<AuthResource> {
  /**
   * Dane zalogowanego użytkownika
   *
   * @memberof AuthModule
   */
  public state = {
    data: undefined,
    permissions: {}
  };

  public mutations = {
    /**
     * Zapisuje uprawnienia
     *
     * @param {*} localState
     * @param {{permissions: string[], result: boolean[]}} data
     */
    savePermissions(localState: any, data: { permissions: string[]; result: boolean[] }): void {
      const permissionsObj = data.permissions.reduce((obj: any, permission: string, index: number) => {
        obj[permission] = data.result[index];
        return obj;
      }, {});
      localState.permissions = { ...localState.permissions, ...permissionsObj };
    },

    /**
     * Zapisuje dane
     *
     * @param {*} localState
     * @param {*} data
     */
    saveData(localState: any, data: any): void {
      localState.data = data;
    },

    /**
     * Czyści uprawnienia
     *
     * @param {*} localState
     */
    clearPermissions(localState: any): void {
      localState.permissions = {};
    },

    /**
     * Czyści zapisane dane użytkownika
     *
     * @param {*} localState
     */
    clearData(localState: any): void {
      localState.data = undefined;
    }
  };
  public actions = {
    /**
     * Logowanie
     *
     * @param context context
     * @param data dane logowania
     */
    login: async (context: any, data: Login): Promise<void> => {
      const response = await this.resource.login(data.login, data.password);
      localStorage.setItem(Storage.TOKEN, response.token);
      if (response.profile.id) {
        localStorage.setItem(Storage.PROFILE_ID, response.profile.id.toString());
      }
      context.commit('saveData', response.profile);
    },

    /**
     * Wczytuje dane profilu
     *
     * @param context context
     * @param data dane logowania
     */
    loadData: async (context: any): Promise<void> => {
      const profileId = tryParseInt(localStorage.getItem(Storage.PROFILE_ID) as string);
      if (context.state.data || !profileId) {
        return;
      }
      await profileResource.get(profileId).then(profile => context.commit('saveData', profile));
    },

    /**
     * Wylogowanie
     *
     * @param context context
     */
    logout: (context: any) => {
      localStorage.removeItem(Storage.TOKEN);
      localStorage.removeItem(Storage.PROFILE_ID);
      context.commit('clearPermissions');
      context.commit('clearData');
    },
    /**
     * Sprawdza uprawnienia
     *
     * @param context context
     * @param permissions uprawnienia
     */
    checkPermissions: async (context: any, permissions: string | string[]): Promise<Dictionary<boolean>> => {
      if (!Array.isArray(permissions)) {
        permissions = [permissions];
      }

      const toCheck = permissions.filter(permission => !context.state.permissions[permission]);
      if (toCheck.length) {
        await this.resource
          .checkPermissions(toCheck)
          .then(result => context.commit('savePermissions', { permissions: toCheck, result }));
      }

      return permissions.reduce(
        (obj, permission) => ({ ...obj, [permission]: context.state.permissions[permission] }),
        {}
      );
    },

    /**
     * Zmiana hasła zalogowanego użytkownika
     *
     * @param {*} context
     * @param {{password: string, currentPassword: string}} data
     * @returns {Promise<boolean>}
     */
    setPassword: (context: any, data: { password: string; currentPassword: string }): Promise<boolean> => {
      const profileId = tryParseInt(localStorage.getItem(Storage.PROFILE_ID) as string);
      if (!profileId) {
        throw new Error('Użytkownik nie jest zalogowany');
      }
      return this.resource.setPasswordLogged(tryParseInt(profileId) as number, data);
    }
  };
}

export default new AuthModule(authResource);
